import { z } from 'zod';
import { claveCatalogo } from './catalogos.js';
import { centavos, monedaIso } from './comercial.js';
/**
 * Inventario, guias y rastreo.
 */
export const TIPOS_ARTICULO = ['ART_EQUIPO', 'ART_REFACCION', 'ART_CONSUMIBLE'];
export const TIPOS_ALMACEN = ['ALM_CEDIS', 'ALM_RESGUARDO'];
export const TIPOS_MOVIMIENTO = [
    'MOV_ENTRADA',
    'MOV_SALIDA',
    'MOV_TRASPASO',
    'MOV_DOTACION',
    'MOV_INSTALADO',
    'MOV_RETIRADO',
    'MOV_REFACCION',
    'MOV_AJUSTE',
];
export const ESTADOS_GUIA = [
    'GUIA_GENERADA',
    'GUIA_RECOLECTADA',
    'GUIA_TRANSITO',
    'GUIA_ENTREGADA',
    'GUIA_INCIDENCIA',
];
/** Clases de captura de inventario en el ticket. */
export const CLASES_INVENTARIO_TICKET = ['INSTALADO', 'RETIRADO', 'REFACCION'];
/* ------------------------------- Articulos ------------------------------- */
export const articuloAlta = z.object({
    cliente_id: z.string().uuid(),
    clave: claveCatalogo('art'),
    /* Obligatorio y unico por cuenta: es como el cliente nombra la pieza, y dos
       clientes pueden usar el mismo numero para cosas distintas. */
    numero_parte: z.string().trim().min(1).max(60),
    codigo_barras: z.string().trim().min(4).max(60).optional(),
    nombre: z.string().trim().min(2).max(200),
    tipo: z.enum(TIPOS_ARTICULO),
    unidad: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2,6}$/)
        .optional(),
    controla_serie: z.boolean().optional(),
    costo_unitario_centavos: centavos.optional(),
    moneda: monedaIso.optional(),
    comentarios_manejo: z.string().trim().max(2000).optional(),
    activo: z.boolean().optional(),
});
export const articuloEdicion = articuloAlta.partial().omit({ clave: true, cliente_id: true });
/* ------------------------------- Almacenes ------------------------------- */
const almacenCampos = z.object({
    clave: claveCatalogo('alm'),
    nombre: z.string().trim().min(2).max(160),
    tipo: z.enum(TIPOS_ALMACEN),
    ciudad_id: z.string().uuid().optional(),
    idc_id: z.string().uuid().optional(),
    surte_de_id: z.string().uuid().optional(),
    direccion: z.string().trim().max(400).optional(),
    latitud: z.number().min(-90).max(90).optional(),
    longitud: z.number().min(-180).max(180).optional(),
    activo: z.boolean().optional(),
});
export const almacenAlta = almacenCampos.refine((a) => (a.tipo === 'ALM_RESGUARDO') === (a.idc_id !== undefined), { message: 'Un resguardo pertenece a un IDC, y solo un resguardo lleva IDC' });
/* La edicion no permite cambiar el tipo ni el IDC: un resguardo que cambia de
   dueno o de naturaleza es un almacen nuevo, y moverlo deja las existencias
   atribuidas a quien ya no las tiene. */
export const almacenEdicion = almacenCampos
    .partial()
    .omit({ clave: true, tipo: true, idc_id: true });
/* ------------------------------ Movimientos ------------------------------ */
export const renglonMovimiento = z.object({
    articulo_id: z.string().uuid(),
    cantidad: z.number().int().positive().max(100_000),
    /* Una serie por pieza cuando el articulo las controla. El motivo de que
       sean renglones y no un texto: se exige una por pieza y no se repiten. */
    series: z.array(z.string().trim().min(3).max(80)).max(1000).optional(),
});
export const movimientoAlta = z
    .object({
    tipo: z.enum(TIPOS_MOVIMIENTO),
    almacen_origen_id: z.string().uuid().optional(),
    almacen_destino_id: z.string().uuid().optional(),
    motivo_ajuste_id: z.string().uuid().optional(),
    /* Sentido del ajuste: falta (-1) o sobra (1). */
    sentido_ajuste: z.union([z.literal(-1), z.literal(1)]).optional(),
    ticket_id: z.string().uuid().optional(),
    comentario: z.string().trim().max(2000).optional(),
    renglones: z.array(renglonMovimiento).min(1).max(200),
})
    .refine((m) => m.tipo !== 'MOV_AJUSTE' ||
    (m.motivo_ajuste_id !== undefined && m.sentido_ajuste !== undefined), { message: 'Un ajuste exige motivo del catalogo y sentido (falta o sobra)' })
    .refine((m) => m.almacen_origen_id === undefined || m.almacen_origen_id !== m.almacen_destino_id, { message: 'El origen y el destino no pueden ser el mismo almacen' });
/* --------------------------------- Guias --------------------------------- */
export const guiaAlta = z.object({
    numero_guia: z.string().trim().min(4).max(60),
    paqueteria_id: z.string().uuid(),
    almacen_origen_id: z.string().uuid(),
    almacen_destino_id: z.string().uuid(),
    renglones: z.array(renglonMovimiento).min(1).max(200),
    /* Para que tickets viaja el material. Pueden ser varios y de clientes
       distintos: cuando pasa, es un envio consolidado. */
    tickets: z.array(z.string().uuid()).max(50).optional(),
});
export const guiaRecepcion = z.object({
    recibido_por: z.string().trim().min(3).max(120),
});
export const guiaIncidencia = z.object({
    nota: z.string().trim().min(3).max(2000),
    /* Devolver al origen regresa las piezas al inventario de salida. */
    devolver_al_origen: z.boolean().optional(),
});
/* ------------------- Captura de inventario en el ticket ------------------ */
export const inventarioTicket = z.object({
    clase: z.enum(CLASES_INVENTARIO_TICKET),
    renglones: z.array(renglonMovimiento).min(1).max(200),
    comentario: z.string().trim().max(2000).optional(),
});
//# sourceMappingURL=inventario.js.map